import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  InlineLoading,
  InlineNotification,
  Layer,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  TextInputSkeleton,
} from '@carbon/react';
import { FaqRow, FaqsEditor, toFaqPayload, toFaqRows } from 'src/components/Storefront/FaqsEditor';
import { GalleryEditor, GalleryRow, toGalleryPayload, toGalleryRows } from 'src/components/Storefront/GalleryEditor';
import {
  TestimonialRow,
  TestimonialsEditor,
  toTestimonialPayload,
  toTestimonialRows,
} from 'src/components/Storefront/TestimonialsEditor';
import { useClearSavedState } from 'src/hooks/useClearSavedState';
import { useStorefront, useUpdateStorefrontMutation } from 'src/hooks/useStorefront';
import { StorefrontUpdateDto } from 'src/services/apis/types';

/**
 * The screen's payload policy, in one place. Exported because it is the only statement of that
 * policy: each list is normalised by its own editor's builder, and every list is sent every time.
 * The API replaces each array wholesale, so a save that sent only the list the owner touched would
 * leave the other two to the server's guess. `Extras.test.tsx` drives it through the DOM.
 */
export function toExtrasPayload(
  gallery: GalleryRow[],
  testimonials: TestimonialRow[],
  faqs: FaqRow[]
): StorefrontUpdateDto {
  return {
    gallery: toGalleryPayload(gallery),
    testimonials: toTestimonialPayload(testimonials),
    faqs: toFaqPayload(faqs),
  };
}

interface ExtrasEditorProps {
  gallery: GalleryRow[];
  testimonials: TestimonialRow[];
  faqs: FaqRow[];
  saving: boolean;
  saved: boolean;
  error: unknown;
  onSave: (payload: StorefrontUpdateDto) => void;
}

export const ExtrasEditor = React.memo(
  ({
    gallery: storedGallery,
    testimonials: storedTestimonials,
    faqs: storedFaqs,
    saving,
    saved,
    error,
    onSave,
  }: ExtrasEditorProps) => {
    const { t } = useTranslation();
    // Seeded once from the loaded row and owned from then on: the refetch a save triggers must not
    // yank a half-typed caption out from under the owner. Same shape as SectionsEditor.
    const [gallery, setGallery] = useState(storedGallery);
    const [testimonials, setTestimonials] = useState(storedTestimonials);
    const [faqs, setFaqs] = useState(storedFaqs);

    const handleSubmit = useCallback(
      (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSave(toExtrasPayload(gallery, testimonials, faqs));
      },
      [onSave, gallery, testimonials, faqs]
    );

    return (
      <Form onSubmit={handleSubmit}>
        <Stack gap={7}>
          {/* One list at a time rather than three stacked. Carbon keeps every panel mounted (only
              hidden), and the lists' state lives here, so switching tabs loses nothing and Save
              still sends all three. A contained panel is itself on $layer-01, so the `Layer`
              puts the item cards one step above it. */}
          <Tabs>
            <TabList aria-label={t('storefront.extras.lists')} contained>
              <Tab>{t('storefront.extras.gallery')}</Tab>
              <Tab>{t('storefront.extras.testimonials')}</Tab>
              <Tab>{t('storefront.extras.faqs')}</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Layer>
                  <GalleryEditor items={gallery} onChange={setGallery} />
                </Layer>
              </TabPanel>
              <TabPanel>
                <Layer>
                  <TestimonialsEditor items={testimonials} onChange={setTestimonials} />
                </Layer>
              </TabPanel>
              <TabPanel>
                <Layer>
                  <FaqsEditor items={faqs} onChange={setFaqs} />
                </Layer>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <div className={'storefront-extras__actions'}>
            {saving || saved ? (
              <InlineLoading
                description={saved ? t('common.saved') : `${t('common.saving')}...`}
                status={saved ? 'finished' : 'active'}
              />
            ) : (
              <Button type="submit">{t('storefront.actions.save')}</Button>
            )}
          </div>

          {error ? <InlineNotification kind={'error'} subtitle={t('messages.somethingWentWrong')} /> : null}
        </Stack>
      </Form>
    );
  }
);

ExtrasEditor.displayName = 'ExtrasEditor';

const StorefrontExtras = React.memo(() => {
  const { t } = useTranslation();
  const { data: storefront, isLoading } = useStorefront();
  const {
    mutate: updateStorefront,
    isLoading: saving,
    isSuccess: saved,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateStorefrontMutation();

  useClearSavedState(saved, resetUpdate);

  return (
    <Stack gap={6}>
      <p className={'storefront-extras__hint'}>{t('storefront.extras.hint')}</p>
      {isLoading && <TextInputSkeleton />}
      {!isLoading && storefront && (
        <ExtrasEditor
          gallery={toGalleryRows(storefront.gallery)}
          testimonials={toTestimonialRows(storefront.testimonials)}
          faqs={toFaqRows(storefront.faqs)}
          saving={saving}
          saved={saved}
          error={updateError}
          onSave={updateStorefront}
        />
      )}
    </Stack>
  );
});

export default StorefrontExtras;
